import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@curi/react';
import { Helmet } from 'react-helmet';
import { Curious } from '@curi/react';
import __map from 'lodash/map';

import './EducatorsWrapper.css';
import { gtmPageviewEvent } from '../../services/google-tag-manager';
import PageHeader from '../../components/global/PageHeader';
import Button from '../../components/elements/Button';
import Video from '../../components/global/Video';

const imgURL = process.env.REACT_APP_CLOUDFRONT_URL;

class Educators extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      faq: [
        {
          question: 'How much is codeSpark?',
          answer: 'codeSpark is 100% FREE for verified teachers and librarians of public schools in North America and non-profit organizations.'
        },
        {
          question: 'Is codeSpark free for Homeschool use?',
          answer: 'Homeschooling parents will have to pay for a subscription to codeSpark at a discounted rate. Please contact us at <a href="mailto:info@codespark.com">info@codespark.com</a> for more information.'
        },
        {
          question: 'Does codeSpark’s curriculum align to standards?',
          answer: 'Yes - codeSpark aligns to Math, Science, and Computer Science standards.'
        },
        {
          question: 'Is there a limit to how many students or classrooms I can create in the teacher dashboard?',
          answer: 'No, you can create unlimited classrooms and students.'
        },
        {
          question: 'I accidently signed up for a Parent account. How can I sign up for the teacher dashboard?',
          answer: 'Contact us at <a href="mailto:info@codespark.com">info@codespark.com</a> and we’ll be able to help transfer your account.'
        },
        {
          question: 'Can my students play at home using their school account?',
          answer: 'Unfortunately, no. Teacher accounts are not transferable to at-home use.'
        }
      ]
    }
  }

  componentDidMount() {
    gtmPageviewEvent({
      pageName: 'codespark.com: Teacher Landing',
      componentProps: this.props,
    });
  }

  trackDashboardHomeClick() {
    try {
      this.context.mixpanel.track('codespark.com: Hour of Code Teacher Dash Clicked');
    } catch (e) {
      console.error(e);
    }
  }

  openDashboardRegister() {
    try {
      this.context.mixpanel.track('codespark.com: Hour of Code Teacher Dash Clicked');
    } catch (e) {
      console.error(e);
    }
    window.open(process.env.REACT_APP_DASHBOARD_BASE_URL + '/register', '_blank');
  }

  render() {
    return (
      <div className="page educators-page">
        <Helmet>
            <title>Coding for K-5 Students | Educator Resources | codeSpark</title>
            <meta name="description" content="Free access to the award-winning learn to code educational game for early learners: kindergarten - 3rd grade. Used in over 35,000 schools, teachers receive free standards-backed curriculum, specialized Hour of Code curriculum, lesson plans and educator resources." />
            <meta name="keywords" content="Hour of code, free coding programs, coding for kids, kids apps, kids games, coding in the classroom, STEM" />
            <meta property="og:title" content="Coding Game for Kids - Try it Free!" />
            <meta property="og:description" content="Try the #1 learn-to-code app for kids ages 3-10. Used by over 20 million kids worldwide, kids learn coding basics with puzzles and build skills to make their own games." />
            <meta name="twitter:card" content="app" />
            <meta name="twitter:site" content="@codesparkapp" />
            <meta name="twitter:description" content="Try the #1 learn-to-code app for kids 4+. Used by over 20 Million kids, codeSpark teaches coding basics through creative play and game creation. Coding improves STEM, reading, and math skills." />
            <meta name="twitter:app:country" content="US" />
            <meta name="twitter:app:id:iphone" content="923441570" />
            <meta name="twitter:app:id:ipad" content="923441570" />
            <meta name="twitter:app:id:googleplay" content="org.codespark.thefoos" />
        </Helmet>
        <div className="teacher-landing page">
          <div id="top-section">
            <div className="section">
              <img alt="" className="schoolhouse" src={`${imgURL}/flagship/teacher-landing/schoolhouse_bg.png`} aria-hidden="true" />
              <PageHeader text="codeSpark for Educators" />
              <h2 className="subtitle">The best learn-to-code program for K-5 teachers!</h2>
              <div className="container">
                <Video
                    videoUrl={`${process.env.REACT_APP_S3_BASE_URL}/flagship/videos/teacher_landing.mp4`}
                    title="Teacher Dashboard" extraClass="teacher-video"
                    poster={`${imgURL}/flagship/teacher-landing/teacher_video_poster.jpg`}
                />
                <div id="sign-up">
                  <h2 className="header">Free for verified public schools in North America<br />Used in over 35,000 schools worldwide!</h2>
                  <Button text="Teachers Start Here" type="in-page" onClick={(e) => this.openDashboardRegister(e)} />
                  <p className="some-points">
                    Kid Safe&trade; Certified
                    <br />
                    Promotes creativity and making
                    <br />
                    Accessible by all types of learners
                  </p>
                  <img alt="Available on iOS, Android and Amazon Kindle" src={`${imgURL}/flagship/teacher-landing/educators_devices_updated.png`} />
                </div>
              </div>
            </div>
          </div>
          <div id="what-you-get" className="grid-x grid-padding-x">
            <div className="get-points">
              <img alt="" src={`${imgURL}/flagship/teacher-landing/teacher-2.png`} />
              <h2 className="header left">Sign up for our Teacher<br className="show-for-medium" />Dashboard and get:</h2>
              <ul className="list">
                <li>FREE curriculum, unplugged activities and more</li>
                <li>Easy classroom setup</li>
                <li>Manage students</li>
                <li>Control access for students</li>
              </ul>
            </div>
            <div className="imageContainer">
              <img alt="" src={`${imgURL}/flagship/teacher-landing/dashboard.png`} className="dashboard" />
            </div>
          </div>
          <div id="quote">
            <p className="quote">
              My kids are in love with The Foos! I have been working on the
              site this week with Kindergartners and First Graders. Thanks for
              creating such an engaging way to teach coding!
            </p>
          </div>
          <div id="benefits-of-teaching">
            <h2 className="header left">Benefits of teaching code with codeSpark</h2>
            <ul className="list">
              <li>Aligns to standards in Math, Science, and Computer Science.</li>
              <li>Based on curriculum developed at MIT and UCLA.</li>
              <li>Completely word-free, perfect for pre-readers.</li>
              <li>New content added monthly.</li>
              <li>More than &ldquo;move the robot&rdquo;. Kids apply coding skills to make games.</li>
            </ul>
          </div>
          <div id="proven-effective">
            <h2 className="header left">codeSpark is proven effective</h2>
            <p className="description">After 90 minutes of coding with codeSpark</p>
            <p className="statistic"><span>22%</span> showed an increase in accuracy with moderate sequencing tasks</p>
            <p className="statistic"><span>55%</span> showed an increase confidence in problem-solving abilities</p>
          </div>
        </div>
        <div className="hoc-page">
          <div className="educators-resources-section hoc-section" id="resources">
            <div className="intro-container">
              <h2 className="section-title">Teacher Dashboard</h2>
              <h3 className="resources-subtitle">MANAGE YOUR CLASS</h3>
              <Button text="GET STARTED" type="in-page" onClick={(e) => this.openDashboardRegister(e)} />
            </div>
            <div className="downloads-container">
              <div className="downloads">
                <a href={`${process.env.REACT_APP_DASHBOARD_BASE_URL}/register`} target="_blank" rel="noopener noreferrer" onClick={() => this.trackDashboardHomeClick()}>
                  <div className="download-block">
                    <div>
                      <img alt="" className="download-block--download-image" src={`${imgURL}/flagship/download_symbol.png`} aria-hidden="true" />
                    </div>
                    <p className="download-block--title">Getting<br className="only-medium-up" /> Started Guide</p>
                  </div>
                </a>
                <a href={`${process.env.REACT_APP_DASHBOARD_BASE_URL}/register`} target="_blank" rel="noopener noreferrer" onClick={() => this.trackDashboardHomeClick()}>
                  <div className="download-block">
                    <div>
                      <img alt="" className="download-block--download-image" src={`${imgURL}/flagship/download_symbol.png`} aria-hidden="true" />
                    </div>
                    <p className="download-block--title">Unplugged Activities</p>
                  </div>
                </a>
                <a href={`${process.env.REACT_APP_DASHBOARD_BASE_URL}/register`} target="_blank" rel="noopener noreferrer" onClick={() => this.trackDashboardHomeClick()}>
                  <div className="download-block">
                    <div>
                      <img alt="" className="download-block--download-image" src={`${imgURL}/flagship/download_symbol.png`} aria-hidden="true" />
                    </div>
                    <p className="download-block--title">Solutions Guide</p>
                  </div>
                </a>
                <a href={`${process.env.REACT_APP_DASHBOARD_BASE_URL}/register`} target="_blank" rel="noopener noreferrer" onClick={() => this.trackDashboardHomeClick()}>
                  <div className="download-block">
                    <div>
                      <i className="fa fa-link download-block--link-icon" aria-hidden="true" />
                    </div>
                    <p className="download-block--title">Manage Classroom</p>
                  </div>
                </a>
                <a href="https://teach.codespark.com/courses/codespark-ignite" target="_blank" rel="noopener noreferrer">
                  <div className="download-block">
                    <div>
                      <i className="fa fa-link download-block--link-icon" aria-hidden="true" />
                    </div>
                    <p className="download-block--title">Professional Development</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div id="hour-of-code" className="section">
          <h2 className="header">Hour of Code</h2>
          <p>
            codeSpark participates in the annual <Link to="HourOfCode" className="learn-more">Hour of Code&trade;</Link> event
            and is a partner of code.org, CSTA, and CSTA movement. Hour of Code activities are completely FREE,
            and come with specialized curriculum to help teachers plan their event.
          </p>
          <Link to="HourOfCode" className="learn-more">Learn More</Link>
        </div>
        <div id="who-we-work-with" className="section image-list">
          <h2 className="header">Who we work with</h2>
          <ul>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/peanuts.jpg`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/cs-for-all.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/iste.jpg`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/ucla-cresst.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/steam-coders.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/breteau.jpg`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/code-org.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/everyone-can-code.jpg`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/csta.jpg`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/plan-ceibal.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/partners/nyc.png`} /></li>
          </ul>
        </div>

        <div id="awards-and-recognition" className="section image-list">
          <h2 className="header">Awards and recognition</h2>
          <ul>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/awards/AASL_Digital+Tools+Circle_2020.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/awards/brain-toy.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/awards/ctr.jpg`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/awards/kes.png`} /></li>
            <li><img alt="" src={`${imgURL}/flagship/teacher-landing/awards/reimagine.png`} /></li>
            <li>
              <a href="https://www.kidsafeseal.com/certifiedproducts/codespark_thefoos_app.html" target="_blank" rel="noopener noreferrer">
                <img border="0" alt="codeSpark - coding for kids is certified by the kidSAFE Seal Program." src="https://www.kidsafeseal.com/sealimage/1730524102862329251/codespark_thefoos_app_large_whitetm.png" />
              </a>
            </li>
            <li><img alt="" src={`${imgURL}/flagship/how-it-works/awards/parents_choice_gold.png`} /></li>
          </ul>
        </div>

        <div id="more-press" className="section">
          <p className="quote">My kids are in love with The Foos! I have been working on the site this week with Kindergartners and First Graders. Thanks for creating such an engaging way to teach coding!</p>
          <div className="more-link"><Link to="Press">More Press &gt;&gt;</Link></div>
        </div>

        <div id="frequently-asked-questions">
          <h2 className="header">Frequently Asked Questions</h2>
          {
            //show all flags in the same group
            this.state.faq.length > 0 &&
            __map(this.state.faq, (faq, i) => {
              return (
                <div key={i} className="set">
                  <p className="question">{faq.question}</p>
                  <p className="answer" dangerouslySetInnerHTML={{ __html: faq.answer }}></p>
                </div>
              )
            })
          }
        </div>
      </div>
    );
  }
}

Educators.contextTypes = {
  mixpanel: PropTypes.object
};

export default props => (
  <Curious>
    {({ router, response, navigation }) => {
      return <Educators router={router} response={response} {...props} />;
    }}
  </Curious>
);



// WEBPACK FOOTER //
// ./src/containers/Educators/index.js